import React from 'react';

export const NotFoundSvg: React.FC<{ className?: string }> = ({ className = 'w-72 h-48 sm:w-96 sm:h-64' }) => {
  return (
    <svg
      viewBox="0 0 500 300"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`${className} mx-auto transition-all duration-300`}
    >
      <style>{`
        @keyframes floatAstronaut {
          0%, 100% {
            transform: translate(250px, 115px) rotate(0deg);
          }
          50% {
            transform: translate(250px, 106px) rotate(1.5deg);
          }
        }
        @keyframes floatSaturn {
          0%, 100% {
            transform: translate(390px, 65px) rotate(-20deg);
          }
          50% {
            transform: translate(393px, 57px) rotate(-16deg);
          }
        }
        @keyframes waveMap {
          0%, 100% {
            transform: translate(-5px, 18px) rotate(-6deg);
          }
          50% {
            transform: translate(-4px, 16px) rotate(-2deg);
          }
        }
        @keyframes twinkleStar1 {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes twinkleStar2 {
          0%, 100% { opacity: 0.9; transform: scale(1.2); }
          50% { opacity: 0.2; transform: scale(0.7); }
        }
        .anim-astronaut {
          animation: floatAstronaut 4.5s ease-in-out infinite;
          transform-origin: center;
        }
        .anim-saturn {
          animation: floatSaturn 6s ease-in-out infinite;
          transform-origin: center;
        }
        .anim-map {
          animation: waveMap 3s ease-in-out infinite;
          transform-origin: top left;
        }
        .anim-star-1 {
          animation: twinkleStar1 2.5s ease-in-out infinite;
          transform-box: fill-box;
          transform-origin: center;
        }
        .anim-star-2 {
          animation: twinkleStar2 3.5s ease-in-out infinite;
          transform-box: fill-box;
          transform-origin: center;
        }
      `}</style>

      {/* ─── Space Stars & Distant Celestial Objects ───────────────────── */}
      {/* Stars with Twinkle Animation */}
      <circle cx="60" cy="40" r="2.5" fill="#2ba832" className="anim-star-1" />
      <circle cx="120" cy="70" r="1.5" fill="#94A3B8" className="anim-star-2" />
      <circle cx="420" cy="45" r="2.5" fill="#0066b2" className="anim-star-1" />
      <circle cx="450" cy="110" r="2" fill="#F59E0B" className="anim-star-2" />
      <circle cx="70" cy="150" r="2" fill="#0066b2" className="anim-star-2" />
      <circle cx="430" cy="165" r="2.5" fill="#2ba832" className="anim-star-1" />

      {/* Cross Starbursts */}
      <path d="M70 50 V60 M65 55 H75" stroke="#0066b2" strokeWidth="2" strokeLinecap="round" className="anim-star-1" />
      <path d="M430 70 V80 M425 75 H435" stroke="#2ba832" strokeWidth="2" strokeLinecap="round" className="anim-star-2" />

      {/* Distant Saturn Ringed Planet with Floating Animation */}
      <g className="anim-saturn">
        <ellipse cx="0" cy="0" rx="20" ry="6" stroke="#2ba832" strokeWidth="3" fill="none" opacity="0.7" />
        <circle cx="0" cy="0" r="10" fill="#0066b2" className="dark:fill-blue-500" />
      </g>

      {/* Distant Earth/Moon Globe */}
      <circle cx="75" cy="80" r="18" fill="#E2E8F0" className="dark:fill-slate-800" opacity="0.5" />
      <circle cx="70" cy="75" r="5" fill="#CBD5E1" className="dark:fill-slate-700" opacity="0.5" />
      <circle cx="83" cy="86" r="3" fill="#CBD5E1" className="dark:fill-slate-700" opacity="0.5" />

      {/* ─── Circular Planet Sphere Ground ──────────────────────────────── */}
      <g>
        {/* Main Planet Curved Body */}
        <ellipse
          cx="250"
          cy="385"
          rx="230"
          ry="165"
          fill="#CBD5E1"
          className="dark:fill-slate-800"
        />

        {/* Inner Curved Shadow Surface */}
        <ellipse
          cx="250"
          cy="392"
          rx="228"
          ry="160"
          fill="#94A3B8"
          className="dark:fill-slate-900"
          opacity="0.8"
        />

        {/* Circular Craters following Planet Horizon Curvature */}
        <ellipse cx="130" cy="242" rx="16" ry="6" fill="#64748B" className="dark:fill-slate-950" opacity="0.4" transform="rotate(-15 130 242)" />
        <ellipse cx="370" cy="245" rx="18" ry="6" fill="#64748B" className="dark:fill-slate-950" opacity="0.4" transform="rotate(15 370 245)" />
        <ellipse cx="250" cy="252" rx="26" ry="7" fill="#475569" className="dark:fill-slate-950" opacity="0.5" />
        <ellipse cx="190" cy="275" rx="20" ry="5" fill="#475569" className="dark:fill-slate-950" opacity="0.4" transform="rotate(-8 190 275)" />
        <ellipse cx="320" cy="278" rx="16" ry="5" fill="#475569" className="dark:fill-slate-950" opacity="0.4" transform="rotate(8 320 278)" />
      </g>

      {/* ─── Man / Astronaut Standing on Top of Round Planet (Animated) ──── */}
      <g className="anim-astronaut">
        {/* Shadow on Planet Surface */}
        <ellipse cx="0" cy="108" rx="28" ry="6" fill="#0F172A" opacity="0.35" />

        {/* Boots */}
        <path d="M-18 98 H-4 V108 H-20 Z" fill="#475569" className="dark:fill-slate-400" />
        <path d="M4 98 H18 V108 H2 Z" fill="#475569" className="dark:fill-slate-400" />

        {/* Legs */}
        <rect x="-16" y="63" width="10" height="36" rx="4" fill="#E2E8F0" className="dark:fill-slate-200" stroke="#475569" strokeWidth="3" />
        <rect x="6" y="63" width="10" height="36" rx="4" fill="#E2E8F0" className="dark:fill-slate-200" stroke="#475569" strokeWidth="3" />
        {/* Knee pads */}
        <rect x="-17" y="76" width="12" height="8" rx="2" fill="#2ba832" />
        <rect x="5" y="76" width="12" height="8" rx="2" fill="#2ba832" />

        {/* Backpack */}
        <rect x="-26" y="18" width="52" height="45" rx="8" fill="#64748B" className="dark:fill-slate-600" />

        {/* Suit Body / Torso */}
        <rect x="-20" y="14" width="40" height="52" rx="10" fill="#F8FAFC" className="dark:fill-slate-100" stroke="#475569" strokeWidth="4" />
        {/* Suit Chest Badge */}
        <rect x="-10" y="22" width="20" height="12" rx="3" fill="#0066b2" />

        {/* Left Arm scratching head (Confused / Lost pose) */}
        <path
          d="M-20 20 C-38 8 -35 -17 -16 -20"
          stroke="#F8FAFC"
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
          className="dark:stroke-slate-100"
        />
        <path
          d="M-20 20 C-38 8 -35 -17 -16 -20"
          stroke="#475569"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        {/* Glove scratching helmet */}
        <circle cx="-14" cy="-20" r="6" fill="#2ba832" />

        {/* Right Arm holding map */}
        <path
          d="M20 23 C34 28 38 40 28 48"
          stroke="#F8FAFC"
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
          className="dark:stroke-slate-100"
        />
        <path
          d="M20 23 C34 28 38 40 28 48"
          stroke="#475569"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="28" cy="48" r="6" fill="#2ba832" />

        {/* Astronaut Helmet */}
        <circle cx="0" cy="-8" r="22" fill="#F8FAFC" className="dark:fill-slate-100" stroke="#475569" strokeWidth="4" />
        {/* Glass Visor */}
        <ellipse cx="2" cy="-8" rx="15" ry="12" fill="#0F172A" />
        <ellipse cx="4" cy="-10" rx="10" ry="7" fill="#0066b2" className="dark:fill-blue-500" opacity="0.8" />
        <circle cx="7" cy="-13" r="2.5" fill="#2ba832" />

        {/* ─── Unfolded Paper Map in Hand with "404" (Animated Wave) ─────── */}
        <g className="anim-map">
          {/* Map Base Folds */}
          <polygon
            points="-45,15 -15,10 15,16 45,10 45,65 15,71 -15,65 -45,70"
            fill="#FFFFFF"
            stroke="#334155"
            strokeWidth="3"
            strokeLinejoin="round"
          />
          {/* Map Fold Lines */}
          <line x1="-15" y1="10" x2="-15" y2="65" stroke="#CBD5E1" strokeWidth="2" strokeDasharray="3 3" />
          <line x1="15" y1="16" x2="15" y2="71" stroke="#CBD5E1" strokeWidth="2" strokeDasharray="3 3" />

          {/* Map Header Accent Band */}
          <path d="M-45 15 L-15 10 L15 16 L45 10 V23 L15 28 L-15 22 L-45 27 Z" fill="#2ba832" opacity="0.85" />

          {/* BOLD "404" Written on Map */}
          <text
            x="0"
            y="52"
            textAnchor="middle"
            fill="#0F172A"
            fontSize="26"
            fontWeight="900"
            fontFamily="monospace"
            letterSpacing="2"
          >
            404
          </text>

          {/* Dotted Route & "X" destination mark on Map */}
          <path d="M-30 40 Q-20 30 -5 42 Q10 50 25 35" stroke="#EF4444" strokeWidth="2.5" strokeDasharray="3 2" fill="none" />
          <path d="M22 32 L28 38 M28 32 L22 38" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
        </g>
      </g>
    </svg>
  );
};

export default NotFoundSvg;
